/**
 * Follow-Ups "Reach Out" dialog (approved mock 2026-07-09): pick one or more
 * household members, pick Call Now / Send A Text Message, Proceed.
 *
 * Member selection is multi-select (client 2026-08) — texting works for one
 * or many recipients at once, but a phone call only ever dials one number,
 * so Call Now is only selectable while exactly one member is picked; picking
 * a second member while Call was active clears the method back to
 * unselected (derived each render, not stored — see `activeMethod`).
 *
 * WEB HANDLING: Proceed launches the OS handler via `tel:` / `sms:` — on a
 * phone that's the dialer/messages app; on macOS FaceTime/Messages pick it
 * up; on a desktop with no handler nothing happens, so with exactly one
 * member selected the dialog also shows their number with a Copy button as
 * the guaranteed fallback (hidden once a second member is added — there's no
 * single number left to show). When the in-app Voice/SMS agent module
 * ships, Proceed swaps to it — the selection UI stays.
 *
 * Multi-recipient `sms:` links are NOT cross-platform via a comma-joined
 * path (`sms:a,b`) — Apple's Messages app silently keeps only the first
 * recipient that way. Apple requires the `sms:?addresses=a,b` query-param
 * form instead; everyone else (Android et al.) follows RFC 5724's
 * comma-separated path form. See `buildSmsHref` below.
 *
 * House pattern: shared Dialog primitives (same as the v2 application
 * dialogs); DTO-only (members ride the section's card rows — no fetching).
 * Members without an account phone (children / invited) can't be selected.
 */
import { ArrowRight, Copy, Mail, PhoneOutgoing } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'
import { cn } from '#app/utils/misc'

/** The slice of a member row this dialog reads — DashboardMemberChipData is
 * assignable, and the application-detail page builds these from its own DTO
 * (client 2026-08-27: Reach Out ships on the detail kebab too). */
export interface ReachOutMember {
	key: string
	shortName: string
	fullName: string
	initials?: string
	roleLabel: string
	phone?: string | null
}

type ReachOutMethod = 'call' | 'text'

/** iPadOS Safari reports `platform: 'MacIntel'` (desktop-site default) —
 * `maxTouchPoints > 1` is the standard way to tell it apart from an actual
 * Mac, which reports 0. */
function isApplePlatform(): boolean {
	if (typeof navigator === 'undefined') return false
	return (
		/iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent) ||
		(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
	)
}

function buildSmsHref(recipients: string[]): string {
	const addresses = recipients.join(',')
	return isApplePlatform() ? `sms:?addresses=${addresses}` : `sms:${addresses}`
}

/** `<a>` + programmatic click, not `window.location.href` — more reliably
 * triggers custom URL scheme handlers (tel:/sms:) across browsers, notably
 * Chrome, without a "Frame Load Interrupted" console error. */
function openUrlScheme(href: string) {
	const anchor = document.createElement('a')
	anchor.href = href
	anchor.rel = 'noopener'
	document.body.appendChild(anchor)
	anchor.click()
	anchor.remove()
}

function RadioDot({ selected }: { selected: boolean }) {
	return (
		<span
			aria-hidden="true"
			className={cn(
				'flex size-5 items-center justify-center rounded-full border-2',
				selected ? 'border-[#32302F]' : 'border-[#D6D3D1]',
			)}
		>
			{selected ? (
				<span className="size-2.5 rounded-full bg-[#32302F]" />
			) : null}
		</span>
	)
}

export function ReachOutDialog({
	open,
	onOpenChange,
	members,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	members: ReachOutMember[]
}) {
	// Adults with a dialable number only — children/invited carry phone: null.
	const reachable = members.filter((member) => member.phone)
	const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
	const [method, setMethod] = useState<ReachOutMethod | null>(null)

	// Auto-pick the sole reachable member until the user touches the picker —
	// derived, not stored, so it never fights the user's own selection.
	const effectiveSelectedKeys =
		selectedKeys.size === 0 && reachable.length === 1
			? new Set([reachable[0].key])
			: selectedKeys
	const selectedMembers = reachable.filter((member) =>
		effectiveSelectedKeys.has(member.key),
	)
	const singleSelected =
		selectedMembers.length === 1 ? selectedMembers[0] : null
	// A call only ever dials one number — Call Now is only choosable with
	// exactly one member picked; with "call" already active, adding a second
	// member falls back to unselected rather than staying on an invalid pick.
	const canCall = singleSelected !== null
	const activeMethod = method === 'call' && !canCall ? null : method
	const canProceed =
		selectedMembers.length > 0 &&
		activeMethod !== null &&
		(activeMethod !== 'call' || singleSelected !== null)

	const toggleMember = (key: string) => {
		setSelectedKeys((prev) => {
			const next = new Set(effectiveSelectedKeys)
			if (next.has(key)) next.delete(key)
			else next.add(key)
			return next
		})
	}

	const proceed = () => {
		if (!activeMethod || selectedMembers.length === 0) return
		if (activeMethod === 'call') {
			if (!singleSelected?.phone) return
			openUrlScheme(`tel:${singleSelected.phone}`)
		} else {
			const numbers = selectedMembers
				.map((member) => member.phone)
				.filter((phone): phone is string => Boolean(phone))
			if (numbers.length === 0) return
			openUrlScheme(buildSmsHref(numbers))
		}
		onOpenChange(false)
	}

	const copyNumber = async () => {
		if (!singleSelected?.phone) return
		await navigator.clipboard.writeText(singleSelected.phone)
		toast.success(`${singleSelected.shortName}'s number copied`)
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="rounded-[30px] bg-[#FBFDFD] p-6 sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle className="font-calgary text-left text-[28px] font-medium text-[#32302F]">
						Select For Reach Out
					</DialogTitle>
					<DialogDescription className="sr-only">
						Choose one or more household members, then call or text them.
					</DialogDescription>
				</DialogHeader>

				<div className="rounded-[24px] p-5 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
					<p className="mb-4 text-[17px] text-[#32302F]">
						Select The Person Whom You Want To Reach Out
					</p>
					{reachable.length === 0 ? (
						<p className="text-sm text-[#747474]">
							No member of this household has a phone number on file yet.
						</p>
					) : (
						<div className="flex flex-wrap gap-3">
							{reachable.map((member) => {
								const isSelected = effectiveSelectedKeys.has(member.key)
								return (
									<button
										key={member.key}
										type="button"
										onClick={() => toggleMember(member.key)}
										className={cn(
											'inline-flex items-center justify-center gap-2 rounded-full border py-1.5 pr-4 pl-1.5 transition-colors',
											isSelected
												? 'border-[#32302F]'
												: 'border-[#F0F0F0] hover:border-[#CCCCCC]',
										)}
									>
										<span
											className={cn(
												'flex size-9 items-center justify-center rounded-full border-1 border-[#32302F] bg-[#F0F0F0] text-[13px] font-normal text-[#32302F]',
											)}
										>
											{member.initials}
										</span>
										<span className="text-[15px] text-[#32302F]">
											{member.shortName}
										</span>
									</button>
								)
							})}
						</div>
					)}
				</div>

				<div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
					<button
						type="button"
						disabled={!canCall}
						onClick={() => canCall && setMethod('call')}
						className={cn(
							'flex items-center justify-between rounded-[24px] border px-5 py-3 transition-colors',
							!canCall
								? 'cursor-not-allowed border-[#F0F0F0] opacity-50'
								: activeMethod === 'call'
									? 'border-[#32302F]'
									: 'shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]',
						)}
					>
						<span className="flex items-center gap-3">
							<span className="flex size-10 items-center justify-center rounded-full bg-[#16A3540D] font-normal text-[#16A354]">
								<PhoneOutgoing
									className="size-5 stroke-[1.5px]"
									aria-hidden="true"
								/>
							</span>
							<span className="text-[17px] text-[#32302F]">Call Now</span>
						</span>
						<RadioDot selected={activeMethod === 'call'} />
					</button>
					<button
						type="button"
						onClick={() => setMethod('text')}
						className={cn(
							'flex items-center justify-between rounded-[24px] border px-5 py-3 transition-colors',
							method === 'text'
								? 'border-[#32302F]'
								: 'shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]',
						)}
					>
						<span className="flex items-center gap-3">
							<span className="flex size-10 items-center justify-center rounded-full bg-[#F59F0A0D] font-normal text-[#F59F0A]">
								<Mail className="size-5 stroke-[1.5px]" aria-hidden="true" />
							</span>
							<span className="text-[17px] text-[#32302F]">
								Send A Text Message
							</span>
						</span>
						<RadioDot selected={activeMethod === 'text'} />
					</button>
				</div>

				<div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					{/* Desktop fallback: tel:/sms: need an OS handler, so the number
					    itself is always one click away — only shown for exactly one
					    selected member (multiple selected = no single number to show). */}
					{singleSelected?.phone ? (
						<button
							type="button"
							onClick={() => void copyNumber()}
							className="inline-flex items-center gap-2 self-start rounded-full px-3 py-2 text-sm text-[#747474] transition-colors hover:bg-[#F5F5F5] hover:text-[#32302F] sm:self-auto"
						>
							<Copy className="size-4" aria-hidden="true" />
							{singleSelected.phone}
						</button>
					) : (
						<span className="hidden sm:block" />
					)}
					<button
						type="button"
						onClick={proceed}
						disabled={!canProceed}
						className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-[#32302F] px-8 py-3 text-[16px] font-semibold text-white transition-colors hover:bg-[#1a1918] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
					>
						Proceed
						<ArrowRight className="size-4" aria-hidden="true" />
					</button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
